<template>
	<Transition name="fade">
		<PageLoaderComponent v-if="loading" />

		<div
			v-else
			class="boundary-plus"
		>
			<BoundaryPlusUpsellComponent
				v-if="show_upsell"
				:project="project"
			/>

			<template v-else>
				<div class="boundary-plus__top">
					<h2 class="boundary-plus__heading">
						Boundary +
					</h2>

					<button
						v-if="viewMode === 'staff' && is_staff_admin"
						class="button button--fill"
						:disabled="!has_checked_items"
						@click="show_delete_confirm = true"
					>
						Delete image/video
					</button>
				</div>

				<div
					v-if="!groups_with_items.length && !loading && !is_staff_admin"
					class="boundary-plus__empty"
				>
					<p>No Boundary + content yet.</p>
				</div>

				<div
					v-else
					class="boundary-plus__groups"
				>
					<div
						v-for="group in groups_with_items"
						:key="group.month"
						class="boundary-plus__group"
					>
						<div class="boundary-plus__group-header">
							<div class="boundary-plus__group-title-row">
								<h3 class="boundary-plus__group-month">
									{{ group.month_name }}
								</h3>
								<span class="boundary-plus__group-year">{{ group.year }}</span>
							</div>

							<div
								v-if="is_staff_admin"
								class="boundary-plus__group-actions"
							>
								<LightswitchComponent
									label="Publish"
									off-label="Publish"
									:checked="group.is_published"
									:disabled="publishing_month === group.month"
									@change="togglePublishMonth( group )"
								/>
							</div>
						</div>

						<MasonryWall
							v-if="group.masonry_items.length"
							:items="group.masonry_items"
							:column-width="250"
							:gap="8"
							:max-columns="4"
							:key-mapper="key_mapper"
						>
							<template #default="{ item }">
								<BoundaryPlusUploadComponent
									v-if="item.type === 'boundary_plus_upload'"
									:project="project"
									@upload-complete="loadItems"
								/>
								<BoundaryPlusThumbnailComponent
									v-else
									:item="item"
									:is-staff="is_staff_admin"
									:checked="checked_ids.includes( item.id )"
									@open="openModal( group.items, item )"
									@check="toggleCheck"
									@image-loaded="on_image_loaded"
								/>
							</template>
						</MasonryWall>
					</div>
				</div>
			</template>

			<Teleport to="#modal">
				<BoundaryPlusGalleryModalComponent
					v-if="show_modal && modal_options"
					:options="modal_options"
					@close-modal="closeModal"
				/>
			</Teleport>

			<div
				v-if="show_delete_confirm"
				class="simple-modal-wrapper"
				role="dialog"
				aria-modal="true"
			>
				<div class="simple-modal">
					<p>Are you sure you want to delete the selected {{ checked_ids.length === 1 ? 'item' : `${checked_ids.length} items` }}?</p>
					<div class="simple-modal__buttons">
						<button
							class="button button--large button--destructive"
							:disabled="deleting"
							@click="deleteCheckedItems"
						>
							{{ deleting ? 'Deleting…' : 'Delete' }}
						</button>
						<button
							class="button button--large button--secondary"
							@click="show_delete_confirm = false"
						>
							Cancel
						</button>
					</div>
				</div>
			</div>
		</div>
	</Transition>
</template>

<script setup>
import { ref, computed, defineProps, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { MasonryWall } from '@yeger/vue-masonry-wall';
import { useDataStore } from '../stores/data.js';
import { USER_GROUP_STAFF } from '../constants.js';

import PageLoaderComponent from './PageLoaderComponent.vue';
import BoundaryPlusUpsellComponent from './BoundaryPlusUpsellComponent.vue';
import BoundaryPlusUploadComponent from './uploads/BoundaryPlusUploadComponent.vue';
import BoundaryPlusThumbnailComponent from './BoundaryPlusThumbnailComponent.vue';
import BoundaryPlusGalleryModalComponent from './modals/BoundaryPlusGalleryModalComponent.vue';
import LightswitchComponent from './LightswitchComponent.vue';

const props = defineProps( {
	project: { required: true, type: Object },
	viewMode: { required: false, type: String, default: 'staff' }
} );

const data_store = useDataStore();
const {
	getBoundaryPlusByProjectId,
	publishBoundaryPlusMonth,
	unpublishBoundaryPlusMonth,
	deleteBoundaryPlusItem,
} = data_store;
const { user_group, user_is_admin } = storeToRefs( data_store );

const items = ref( [] );
const loading = ref( true );
const show_modal = ref( false );
const modal_options = ref( null );
const checked_ids = ref( [] );
const show_delete_confirm = ref( false );
const deleting = ref( false );
const publishing_month = ref( null );

const is_staff_admin = computed( () => {
	return user_group.value === USER_GROUP_STAFF && props.viewMode === 'staff' && user_is_admin.value;
} );

const subscription_is_active = computed( () => {
	const expiry = props.project.boundary_plus_expiry_date;
	if ( !expiry ) {
		return false;
	}
	return new Date( expiry ) > new Date();
} );

const show_upsell = computed( () => {
	if ( user_group.value === USER_GROUP_STAFF && props.viewMode === 'staff' ) {
		return false;
	}
	return !subscription_is_active.value;
} );

const has_checked_items = computed( () => checked_ids.value.length > 0 );

const grouped_months = computed( () => {
	const months = {};

	items.value.forEach( ( item ) => {
		const month = item.month;
		if ( !months[month] ) {
			months[month] = {
				month,
				...formatMonthParts( month ),
				items: [],
				is_published: false
			};
		}
		months[month].items.push( item );
		if ( item.published ) {
			months[month].is_published = true;
		}
	} );

	return Object.values( months ).sort( ( a, b ) => b.month.localeCompare( a.month ) );
} );

const groups_with_items = computed( () => {
	const result = grouped_months.value.map( ( group, group_index ) => {
		const masonry_items = ( group_index === 0 && is_staff_admin.value )
			? [ { type: 'boundary_plus_upload' }, ...group.items ]
			: group.items.filter( item => item.published );
		return { ...group, masonry_items };
	} );

	if ( is_staff_admin.value && result.length === 0 ) {
		const now = new Date();
		const month = `${now.getFullYear()}-${String( now.getMonth() + 1 ).padStart( 2, '0' )}`;
		result.push( {
			month,
			...formatMonthParts( month ),
			items: [],
			is_published: false,
			masonry_items: [ { type: 'boundary_plus_upload' } ]
		} );
	}

	return result;
} );

let redraw_pending = false;
let redraw_timeout = null;

function on_image_loaded() {
	if ( redraw_pending ) { return; }
	clearTimeout( redraw_timeout );
	redraw_timeout = setTimeout( () => {
		redraw_pending = true;
		items.value = [ ...items.value ];
	}, 100 );
}

function key_mapper( item ) {
	if ( item.type === 'boundary_plus_upload' ) { return 'upload'; }
	return item.id;
}

function formatMonthParts( month ) {
	const [year, month_num] = month.split( '-' );
	const date = new Date( parseInt( year ), parseInt( month_num ) - 1, 1 );
	return {
		month_name: date.toLocaleString( 'en-GB', { month: 'long' } ),
		year
	};
}

async function loadItems() {
	loading.value = true;
	checked_ids.value = [];
	redraw_pending = false;
	const response = await getBoundaryPlusByProjectId( parseInt( props.project.id ) );
	if ( response.data ) {
		items.value = response.data.boundaryPlusByProjectId;
	}
	loading.value = false;
}

async function refreshItems() {
	redraw_pending = false;
	const response = await getBoundaryPlusByProjectId( parseInt( props.project.id ) );
	if ( response.data ) {
		items.value = response.data.boundaryPlusByProjectId;
	}
}

function toggleCheck( item ) {
	const index = checked_ids.value.indexOf( item.id );
	if ( index >= 0 ) {
		checked_ids.value.splice( index, 1 );
	} else {
		checked_ids.value.push( item.id );
	}
}

function openModal( group_items, item ) {
	const index = group_items.findIndex( ( i ) => i.id === item.id );
	modal_options.value = group_items.map( ( gi, i ) => ( {
		item_id: gi.id,
		image_url: gi.image_large_url || gi.image_original_url || null,
		video_url: gi.video_url || null,
		selected: i === index
	} ) );
	show_modal.value = true;
}

function closeModal() {
	show_modal.value = false;
	modal_options.value = null;
}

async function deleteCheckedItems() {
	if ( !checked_ids.value.length ) {
		return;
	}
	deleting.value = true;
	for ( const id of checked_ids.value ) {
		await deleteBoundaryPlusItem( id );
	}
	deleting.value = false;
	show_delete_confirm.value = false;
	await loadItems();
}

async function togglePublishMonth( group ) {
	const is_published = group.is_published;
	if ( !is_published ) {
		publishMonth( group.month );
	} else {
		unpublishMonth( group.month );
	}
}

async function publishMonth( month ) {
	publishing_month.value = month;
	await publishBoundaryPlusMonth( parseInt( props.project.id ), month );
	publishing_month.value = null;
	await refreshItems();
}

async function unpublishMonth( month ) {
	publishing_month.value = month;
	await unpublishBoundaryPlusMonth( parseInt( props.project.id ), month );
	publishing_month.value = null;
	await refreshItems();
}

onMounted( () => {
	loadItems();
} );
</script>
